/**
 * Burst Overlay Renderer — visual feedback during the Momentum Burst window.
 *
 * Mirrors DIRenderer structure. Gold/kinetic palette.
 * All visuals follow Sakurai principles from docs/SAKURAI-EFFECTS-GUIDE.md.
 *
 * Elements:
 * 1. Ring — circle around tank, active segments pulse gold
 * 2. Arrow — direction indicator, bold gold lines
 * 3. Trajectory arc — simulated physics path with gravity
 * 4. Ghost — semi-transparent hexagon at predicted landing
 * 5. Timer bar — gold fill depleting
 * 6. Prompt text — "HOLD DIRECTION" / "BURST" / "ROCKETPACK"
 * 7. Vignette — gold-tinted, lighter than DI
 * 8. Momentum cost preview — when upward direction held
 */

import { Container, Text, TextStyle } from 'pixi.js';
import { BURST, PHYSICS, WORLD } from '../constants';
import { drawTankShape } from './shapes';
import { lookupTank } from '../tanks';
import type { Player, GameState } from '../types';
import { getBurstDirection, getBurstPredictedVelocity } from '../game/burst';
import { getGroundY, isPointInTerrain } from '../terrain/grid';
import type { ParticleSystem } from './particles';
import { getBurstHint } from './input-hints';
import { OverlayWindowRenderer } from './overlay-window';

export class BurstRenderer extends OverlayWindowRenderer {
    private momentumCostText: Text;

    constructor(worldContainer: Container, uiContainer: Container, particles: ParticleSystem) {
        super(worldContainer, uiContainer, particles, BURST.PROMPT_FONT_SIZE);

        this.momentumCostText = new Text({
            text: '',
            style: new TextStyle({
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: 14,
                fill: BURST.GOLD,
                stroke: { color: 0x000000, width: 3 },
                align: 'center',
            }),
        });
        this.momentumCostText.anchor.set(0.5, 0.5);
        this.overlayUIContainer.addChild(this.momentumCostText);
    }

    protected drawVignette(): void {
        this.vignetteGfx.clear();
        const w = WORLD.CANVAS_WIDTH;
        const h = WORLD.CANVAS_HEIGHT;

        const steps = 6;
        for (let i = 0; i < steps; i++) {
            const t = i / steps;
            const inset = t * Math.min(w, h) * 0.3;
            const alpha = BURST.VIGNETTE_ALPHA * (1 - t * t);
            this.vignetteGfx.rect(inset, inset, w - inset * 2, h - inset * 2);
            this.vignetteGfx.stroke({ color: 0x1a0a00, width: inset * 0.5, alpha, alignment: 1 });
        }
        this.vignetteGfx.rect(0, 0, w, h);
        this.vignetteGfx.stroke({ color: 0x1a0a00, width: 40, alpha: BURST.VIGNETTE_ALPHA * 0.8, alignment: 1 });
    }

    update(state: GameState, dt: number): void {
        if (!this.active || state.burstActivePlayer < 0) {
            if (this.active) this.hide();
            return;
        }

        const player = state.players[state.burstActivePlayer];
        const input = getBurstDirection(player);
        const hasInput = Math.abs(input.x) > 0.01 || Math.abs(input.y) > 0.01;

        this.drawRing(player, input, hasInput, dt);
        this.drawArrow(player, input, hasInput, dt);
        this.drawTrajectoryArc(player, state.voidY);
        this.drawGhost(player, dt);
        this.drawTimerBar(state.burstWindowTimer, BURST.WINDOW_DURATION, BURST.TIMER_BAR_WIDTH, BURST.TIMER_BAR_HEIGHT, BURST.TIMER_BAR_BG_COLOR);
        this.updatePrompt(player, state.burstWindowTimer, hasInput, input);

        // Particle effects
        this.particles.spawnBurstAmbient(player.x, player.y, player.color);

        if (hasInput) {
            this.particles.spawnBurstInputActive(
                player.x, player.y,
                input.x, input.y,
                player.color,
            );
        }
    }

    private drawRing(
        player: Player, input: { x: number; y: number },
        hasInput: boolean, dt: number,
    ): void {
        this.ringGfx.clear();
        const cx = player.x;
        const cy = player.y;
        const r = BURST.RING_RADIUS;
        const segments = BURST.RING_SEGMENTS;

        const inputAngle = hasInput ? Math.atan2(input.y, input.x) : 0;

        // Pass 1: Dark backing behind active segments (Sakurai: visible on any background)
        if (hasInput) {
            for (let i = 0; i < segments; i++) {
                const a0 = (i / segments) * Math.PI * 2;
                const a1 = ((i + 1) / segments) * Math.PI * 2;
                const midAngle = (a0 + a1) / 2;
                const angleDiff = Math.abs(Math.atan2(Math.sin(midAngle - inputAngle), Math.cos(midAngle - inputAngle)));

                if (angleDiff < Math.PI / 4) {
                    const x0 = cx + Math.cos(a0) * r;
                    const y0 = cy + Math.sin(a0) * r;
                    const x1 = cx + Math.cos(a1) * r;
                    const y1 = cy + Math.sin(a1) * r;
                    this.ringGfx.moveTo(x0, y0);
                    this.ringGfx.lineTo(x1, y1);
                    this.ringGfx.stroke({ color: BURST.GOLD_DARK, width: BURST.RING_WIDTH * 1.5 + 2, alpha: 0.5 });
                }
            }
        }

        // Pass 2: Colored segments on top
        for (let i = 0; i < segments; i++) {
            const a0 = (i / segments) * Math.PI * 2;
            const a1 = ((i + 1) / segments) * Math.PI * 2;
            const midAngle = (a0 + a1) / 2;

            let color: number = BURST.RING_INACTIVE_COLOR;
            let alpha = 0.4;
            let width = BURST.RING_WIDTH;

            if (hasInput) {
                const angleDiff = Math.abs(Math.atan2(Math.sin(midAngle - inputAngle), Math.cos(midAngle - inputAngle)));
                if (angleDiff < Math.PI / 4) {
                    color = BURST.RING_ACTIVE_COLOR;
                    alpha = 0.8 + Math.sin(Date.now() * 0.01) * 0.2;
                    width = BURST.RING_WIDTH * 1.5;
                }
            }

            const x0 = cx + Math.cos(a0) * r;
            const y0 = cy + Math.sin(a0) * r;
            const x1 = cx + Math.cos(a1) * r;
            const y1 = cy + Math.sin(a1) * r;

            this.ringGfx.moveTo(x0, y0);
            this.ringGfx.lineTo(x1, y1);
            this.ringGfx.stroke({ color, width, alpha });
        }
    }

    private drawArrow(
        player: Player, input: { x: number; y: number },
        hasInput: boolean, dt: number,
    ): void {
        this.arrowGfx.clear();
        if (!hasInput) return;

        const cx = player.x;
        const cy = player.y;
        const angle = Math.atan2(input.y, input.x);
        const len = BURST.ARROW_LENGTH;
        const headSize = BURST.ARROW_HEAD_SIZE;

        const startDist = BURST.RING_RADIUS + 5;
        const sx = cx + Math.cos(angle) * startDist;
        const sy = cy + Math.sin(angle) * startDist;
        const ex = cx + Math.cos(angle) * (startDist + len);
        const ey = cy + Math.sin(angle) * (startDist + len);

        // Dark backing (Sakurai: contrast)
        this.arrowGfx.moveTo(sx, sy);
        this.arrowGfx.lineTo(ex, ey);
        this.arrowGfx.stroke({ color: BURST.GOLD_DARK, width: BURST.ARROW_WIDTH + 2, alpha: 0.6 });

        // Gold core
        this.arrowGfx.moveTo(sx, sy);
        this.arrowGfx.lineTo(ex, ey);
        this.arrowGfx.stroke({ color: BURST.RING_ACTIVE_COLOR, width: BURST.ARROW_WIDTH, alpha: 0.9 });

        // Arrowhead
        const perpX = -Math.sin(angle) * headSize * 0.5;
        const perpY = Math.cos(angle) * headSize * 0.5;
        const tipX = ex + Math.cos(angle) * headSize;
        const tipY = ey + Math.sin(angle) * headSize;

        this.arrowGfx.poly([
            ex + perpX, ey + perpY,
            tipX, tipY,
            ex - perpX, ey - perpY,
        ]);
        this.arrowGfx.fill({ color: BURST.RING_ACTIVE_COLOR, alpha: 0.9 });
    }

    private drawTrajectoryArc(player: Player, voidY: number): void {
        this.arcGfx.clear();

        const predicted = getBurstPredictedVelocity(player);
        let px = player.x;
        let py = player.y;
        let vx = predicted.vx;
        let vy = predicted.vy;

        // No impulse = no arc
        if (Math.abs(vx) < 0.01 && Math.abs(vy) < 0.01) return;

        const gravity = PHYSICS.GRAVITY;
        const dotCount = BURST.ARC_DOT_COUNT;
        const stepsPerDot = 3;

        for (let i = 0; i < dotCount; i++) {
            for (let s = 0; s < stepsPerDot; s++) {
                vx *= 0.999;
                vy += gravity;
                px += vx;
                py += vy;
            }

            if (py > voidY || isPointInTerrain(px, py)) break;
            if (px < 0 || px > WORLD.VIRTUAL_WIDTH || py < WORLD.WORLD_TOP) break;

            const t = i / dotCount;
            const alpha = BURST.ARC_ALPHA * (1 - t * 0.6);

            // Soft glow behind
            this.arcGfx.circle(px, py, BURST.ARC_GLOW_RADIUS);
            this.arcGfx.fill({ color: BURST.GOLD, alpha: BURST.ARC_GLOW_ALPHA * (1 - t) });

            // Crisp dot
            this.arcGfx.circle(px, py, BURST.ARC_DOT_RADIUS);
            this.arcGfx.fill({ color: 0xffffff, alpha });

            // Dark ring around dot for visibility
            this.arcGfx.circle(px, py, BURST.ARC_DOT_RADIUS + 1.5);
            this.arcGfx.stroke({ color: BURST.GOLD_DARK, width: 1, alpha: alpha * 0.6 });
        }
    }

    private drawGhost(player: Player, dt: number): void {
        this.ghostGfx.clear();

        const predicted = getBurstPredictedVelocity(player);
        let px = player.x;
        let py = player.y;
        let vx = predicted.vx;
        let vy = predicted.vy;

        // No impulse = no ghost
        if (Math.abs(vx) < 0.01 && Math.abs(vy) < 0.01) return;

        const gravity = PHYSICS.GRAVITY;
        const maxSteps = 300;
        let landed = false;

        for (let i = 0; i < maxSteps; i++) {
            vx *= 0.999;
            vy += gravity;
            px += vx;
            py += vy;

            const groundY = getGroundY(px);
            if (py + PHYSICS.ECB_RADIUS >= groundY) {
                py = groundY - PHYSICS.ECB_RADIUS;
                landed = true;
                break;
            }
            if (px < 0 || px > WORLD.VIRTUAL_WIDTH || py < WORLD.WORLD_TOP) break;
        }

        if (!landed) return;

        const pulse = BURST.GHOST_ALPHA + Math.sin(Date.now() * 0.001 * BURST.GHOST_PULSE_SPEED) * BURST.GHOST_PULSE_AMP;

        // Draw per-tank shape at predicted position
        const tankVisual = lookupTank(player.tankId).visual;
        this.ghostGfx.position.set(px, py);
        this.ghostGfx.alpha = pulse;

        // Wireframe ghost at predicted position (gold)
        drawTankShape(this.ghostGfx, player.tankId, tankVisual.bodyRadius, BURST.GOLD);
        // Bright center dot (readability)
        this.ghostGfx.circle(0, 0, 3);
        this.ghostGfx.fill({ color: 0xffffff });
    }

    private updatePrompt(player: Player, timeRemaining: number, hasInput: boolean, input: { x: number; y: number }): void {
        this.promptText.x = WORLD.CANVAS_WIDTH / 2;
        this.promptText.y = 55;

        const frac = timeRemaining / BURST.WINDOW_DURATION;
        const isUpward = input.y < -0.3;

        if (hasInput) {
            this.promptText.text = isUpward ? 'ROCKETPACK' : 'BURST';
            this.promptText.alpha = 0.7;
        } else {
            this.promptText.text = getBurstHint();
            this.promptText.alpha = Math.min(1, frac * 2);
        }

        // Momentum cost preview for directional burst
        const isHorizontal = !isUpward && (Math.abs(input.x) > 0.3);
        const isBrace = !isUpward && !isHorizontal && input.y > 0.3;
        if (hasInput && (isUpward || isHorizontal || isBrace)) {
            const cost = isUpward ? BURST.UPWARD_MOMENTUM_COST
              : isHorizontal ? BURST.HORIZONTAL_MOMENTUM_COST
              : BURST.BRACE_MOMENTUM_COST;
            this.momentumCostText.text = `-${cost} MOMENTUM`;
            this.momentumCostText.x = WORLD.CANVAS_WIDTH / 2;
            this.momentumCostText.y = 75;
            this.momentumCostText.alpha = 0.8;
            this.momentumCostText.visible = true;
        } else {
            this.momentumCostText.visible = false;
        }
    }
}
